import { useAppBridge } from "./useAppBridge";


interface ApiResponse {
  success: boolean;
  data?: any;
  error?: string;
  message?: string;
  needs_installation?: boolean;
  oauth_url?: string;
}

interface ApiConfig {
  timeout?: number;
  baseUrl?: string;
  attemptCount?: number;
}

export const useApi = (
  config: ApiConfig = {
    timeout: 10000,
    baseUrl: "",
    attemptCount: 0,
  },
) => {
  const { refreshSessionToken } = useAppBridge();

  // Use explicit baseUrl, fallback to environment variable, or empty string for proxy
  // Development: uses Vite proxy (empty string)
  // Production: uses VITE_API_URL from .env.production
  const getApiBaseUrl = (): string => {
    console.log(import.meta.env);

    return `${import.meta.env.VITE_BACK_SERVICE_URL}/api` || "no-service";
  };

  const setBaseApiUrl = (url: string): ApiConfig => {
    config.baseUrl = url || "";
    return config;
  };

  const apiUrl = getApiBaseUrl();

  // App Bridge only authentication - no fallbacks
  const fetchWithAuth = async (
    endpoint: string,
    method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
    body?: any,
  ): Promise<ApiResponse> => {
    const timeout = config.timeout || 10000;
    const attempt = config.attemptCount || 0;

    try {
      // Always require a valid App Bridge session token
      console.log("[useApi] Refreshing session token");
      let authToken = await refreshSessionToken();

      if (!authToken) {
        console.log("[useApi] Refreshed unsuccesfully");
        return {
          success: false,
          error: "Authentication required",
          message:
            "No valid App Bridge session token available. Please reload the app.",
        };
      }

      // Build headers with App Bridge token
      const headers: HeadersInit = {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${authToken}`,
      };

      // Add ngrok header only in development
      if (import.meta.env.MODE === "development") {
        headers["ngrok-skip-browser-warning"] = "true";
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const requestUrl = `${apiUrl}${endpoint}`;
      console.log(`[useApi] Initiating request ${method} ${requestUrl}`);

      const response = await fetch(requestUrl, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      // First, read the response as text to avoid body consumption issues
      const responseText = await response.text();

      // Check if response content looks like JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.error(
          `[useApi] Non-JSON response received (${response.status}):`,
          responseText.substring(0, 200),
        );
        return {
          success: false,
          error: `Server returned non-JSON response (${response.status})`,
          message: `The server returned an unexpected response. Please check the server logs.`,
        };
      }

      // Try to parse JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (jsonError) {
        console.error(`[useApi] Failed to parse JSON response:`, jsonError);
        console.error(
          `[useApi] Response body (first 200 chars):`,
          responseText.substring(0, 200),
        );
        return {
          success: false,
          error: `Failed to parse server response: ${jsonError instanceof Error ? jsonError.message : String(jsonError)}`,
          message: `The server response was not valid JSON.`,
        };
      }

      // we need installation
      if (!response.ok && data && data.oauth_url) {
        console.log(`[useApi] Initiating installation ${response} ${data}`);

        return {
          success: false,
          data,
          needs_installation: true,
        } as ApiResponse;
      }

      return {
        success: true,
        ...data,
      } as ApiResponse;
    } catch (error) {
      console.log(`[useApi] Request error (attempt ${attempt + 1}):`, error);

      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
        message: "Failed to make API request",
      };
    }
  };

  // Handle OAuth installation redirect when needed
  const handleInstallationIfRequired = (response: ApiResponse) => {
    if (response.needs_installation && response.data?.oauth_url) {
      // App Bridge v4: use open() for external redirects (OAuth)
      window.open(response.data.oauth_url, '_top');
      return response;
    }
    return response;
  };

  const Call = async (
    endpoint: string,
    method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
    data?: any,
  ) => {
    const response = await fetchWithAuth(endpoint, method, data);
    return handleInstallationIfRequired(response);
  };

  return {
    Call,

    getApiBaseUrl,
    setBaseApiUrl,
  };
};
